import React, { useMemo } from 'react';
import { gql, TypedDocumentNode } from '@apollo/client';
import { useQuery } from '@apollo/client/react';
import { titleize } from '@moda/portal-stanchions';
import {
  ProductListPageCategoryQuery,
  ProductListPageCategoryQueryVariables
} from '../../generated/types';
import {
  DEFAULT_SORT_OPTIONS,
  NEW_SORT_OPTIONS,
  SALE_SORT_OPTIONS,
  SortOption
} from '../../constants';
import { usePLPParams } from '../../hooks/usePLPParams';
import { PAGE_FILTER_FRAGMENT } from '../../components/PageFilter';
import { findSectionModules } from '../../lib/findSectionModules';
import { PRODUCT_LIST_PAGE_RESULT_FRAGMENT, ProductListing } from '../../components/ProductListing';
import { ModuleSection } from '../../components/ModuleSection';
import { NotFoundPage } from '../NotFoundPage';
import { LazyLoad, EnableLazyLoad } from '../../components/LazyLoad';
import { PUMO_MODULE_FRAGMENT } from '../../components/PumoModule';
import { FacadePageType } from '../../lib/getRuleContextFromSlug';
import { useRuleVariables } from '../../hooks/useRuleVariables';
import { useShowSaleOnPLP } from '../../hooks/useShowSaleOnPLP';
import { useCategoryPLPMeta } from '../../hooks/useCategoryPLPMeta';
import { useUrlParams } from '../../hooks/useUrlParams';

export type RuleFeatureFlag = Record<string, boolean>;

export const PRODUCT_LIST_PAGE_CATEGORY_QUERY: TypedDocumentNode<
  ProductListPageCategoryQuery,
  ProductListPageCategoryQueryVariables
> = gql`
  query ProductListPageCategoryQuery(
    $after: String!
    $attributes: [String]
    $availability: [AvailabilityInput]
    $categoryPath: String
    $countryCode: String
    $designers: [String]
    $dynamicReRanking: Boolean
    $filterColors: [String]
    $materials: [String]
    $noSale: Boolean
    $onSale: Boolean
    $priceRange: PriceRange
    $ruleContexts: [String]
    $rules: [Rule]
    $markdownPercentages: [String]
    $scoring: String
    $section: String!
    $sizes: [String]
    $slug: String!
    $versionId: Int
  ) {
    displayPage(slug: $slug, version_id: $versionId) {
      slug
      name
      targetBuckets {
        name
        modules {
          ...PumoModuleFragment
        }
      }
      meta_description
    }

    categoryResult: product_listing(
      input: {
        attributes: $attributes
        availability: $availability
        category_path: $categoryPath
        country_code: $countryCode
        designers: $designers
        dynamic_reranking: $dynamicReRanking
        filter_colors: $filterColors
        materials: $materials
        on_sale: $onSale
        no_sale: $noSale
        price_range: $priceRange
        rounded_markdown_percentages: $markdownPercentages
        sizes: $sizes
        version_id: $versionId
        scoring: $scoring
        rule_contexts: $ruleContexts
        rules: $rules
      }
      slug: $slug
      section: $section
    ) {
      pageName: page_name
      ...ProductListPageResultFragment
      ...PageFilterFragment
    }
  }
  ${PUMO_MODULE_FRAGMENT}
  ${PRODUCT_LIST_PAGE_RESULT_FRAGMENT}
  ${PAGE_FILTER_FRAGMENT}
`;

const SORT_OPTIONS_MAP: { [key: string]: SortOption[] } = {
  new: NEW_SORT_OPTIONS,
  sale: SALE_SORT_OPTIONS,
  shop: DEFAULT_SORT_OPTIONS
};

export const ProductCategoryListingPage: React.FC = () => {
  const showSaleInPLPs = useShowSaleOnPLP();

  const { params: urlParams } = useUrlParams();
  const section =
    (Array.isArray(urlParams.section) ? urlParams.section[0] : urlParams.section) || 'shop';
  const availableSorts = SORT_OPTIONS_MAP[section] || DEFAULT_SORT_OPTIONS;

  const { queryVariables } = usePLPParams(availableSorts);
  const slug =
    section === 'shop'
      ? queryVariables.categoryPath
      : queryVariables.categoryPath
        ? `${section}/${queryVariables.categoryPath}`
        : section;
  const ruleVariables = useRuleVariables(FacadePageType.Category, slug);
  const {
    data: currentData,
    previousData,
    error,
    loading
  } = useQuery(PRODUCT_LIST_PAGE_CATEGORY_QUERY, {
    variables: {
      ...queryVariables,
      slug: slug,
      section,
      noSale: !showSaleInPLPs && section !== 'sale',
      ...ruleVariables
    }
  });

  const data = currentData ?? previousData;

  const topModules = useMemo(() => findSectionModules(data, 'top'), [data]);
  const headerModules = useMemo(() => findSectionModules(data, 'header'), [data]);
  const bottomModules = useMemo(() => findSectionModules(data, 'bottom'), [data]);

  const { header, title, description } = useCategoryPLPMeta({
    designers: data?.categoryResult.designers?.map(designer => designer?.name)
  });

  if (data && !data.categoryResult) return <NotFoundPage />;

  return (
    <>
      {topModules && <ModuleSection modules={topModules.modules} />}
      {headerModules && <ModuleSection modules={headerModules.modules} />}

      <ProductListing
        title={title}
        description={description}
        headerText={titleize(header)}
        queryResult={data?.categoryResult}
        error={error}
        availableSorts={availableSorts}
        loading={loading}
      />

      {bottomModules && (
        <EnableLazyLoad>
          <LazyLoad>
            <ModuleSection modules={bottomModules.modules} />
          </LazyLoad>
        </EnableLazyLoad>
      )}
    </>
  );
};

export default ProductCategoryListingPage;
